@let message = messageSg();
@let citationDisplay = citationDisplaySg();
@let content = citationDisplay.text;
@let sources = citationDisplay.sources;
@let sender = message.role === 'user' ? 'You' : 'Assistant';
@let sentByUser = message.role === 'user';
@let classModifier = sentByUser ? 'outbound' : 'inbound';
@let expanded = expandedSg();
@let showMoreToggle = showMoreToggleSg();
@let status = message.status;
@let isPending = status === 'pending';
@let isFailed = status === 'failed';
@let info = reasoningInfoSg();
@let files = attachedFilesSg();
@let attachmentsOverlayOpen = attachmentsOverlayOpenSg();
@let sourcesOverlayOpen = sourcesOverlayOpenSg();

<div
  class="atn-message atn-message--{{ classModifier }}"
  [class.atn-message--pending]="isPending"
  [class.atn-message--failed]="isFailed"
>
  <div class="atn-message__header">
    <span class="atn-message__sender">
      {{ sender }}
    </span>
    @if (isPending) {
      <span class="atn-message__dots">
        <span class="atn-message__dot"></span>
        <span class="atn-message__dot"></span>
        <span class="atn-message__dot"></span>
      </span>
    }
  </div>

  @if (files.length === 1) {
    <button
      class="atn-message__attachments-trigger"
      type="button"
      title="Download {{ files[0] }}"
      (click)="fileDownload.emit({ message, file: files[0] })"
    >
      <i class="atn-icon atn-file atn-blue"></i>
      <span
        class="atn-message__attachments-name"
        [title]="files[0]"
        >{{ files[0] }}</span
      >
    </button>
  } @else if (files.length > 1) {
    <button
      class="atn-message__attachments-trigger"
      type="button"
      title="{{ files.length }} attachments"
      #attachmentsOrigin
      (click)="attachmentsOverlayOpenSg.set(!attachmentsOverlayOpen)"
    >
      <i class="atn-icon atn-file atn-blue"></i>
      <span class="atn-message__attachments-count"
        >{{ files.length }} attachments</span
      >
    </button>

    @if (attachmentsOverlayOpen) {
      <atn-ds-attached-overlay
        (backdropClick)="attachmentsOverlayOpenSg.set(false)"
        [origin]="attachmentsOrigin"
        [offsetX]="0"
        [offsetY]="4"
      >
        <div class="atn-message__attachments-overlay">
          <div class="atn-message__attachments-overlay-header">
            <span>Attachments</span>
          </div>
          <div class="atn-message__attachments-overlay-list">
            @for (file of files; track file) {
              <button
                class="atn-message__attachments-overlay-item"
                type="button"
                [title]="file"
                (click)="
                  fileDownload.emit({ message, file });
                  attachmentsOverlayOpenSg.set(false)
                "
              >
                <i
                  class="atn-icon atn-file-excel atn-message__attachments-overlay-item-icon"
                ></i>
                <span class="atn-message__attachments-overlay-item-name">{{
                  file
                }}</span>
                <i
                  class="atn-icon atn-download atn-message__attachments-overlay-item-download"
                ></i>
              </button>
            }
          </div>
        </div>
      </atn-ds-attached-overlay>
    }
  }

  <div
    #contentWrapper
    class="atn-message__content-wrapper"
    [class.atn-message__content-wrapper--expanded]="expanded"
    [style.--atn-message-content-expanded-max-height]="
      contentMaxHeightPxSg() + 'px'
    "
  >
    <atn-markdown
      class="atn-message__content"
      [data]="content ?? ''"
      (ready)="onMarkdownReady()"
    />
  </div>

  @if (showMoreToggle || info.length > 0 || sources.length > 0) {
    <div class="atn-message__actions">
      @if (showMoreToggle) {
        <button
          class="atn-message__toggle atn-message__toggle--{{ classModifier }}"
          type="button"
          [attr.aria-expanded]="expanded"
          (click)="toggleExpandButtonClickHandler()"
        >
          {{ expanded ? 'Show less' : 'Show more' }}
        </button>
      }

      <div class="atn-message__secondary-actions">
        @if (sources.length > 0) {
          <button
            class="atn-message__sources-trigger"
            type="button"
            [title]="
              sources.length +
              ' source' +
              (sources.length === 1 ? '' : 's')
            "
            #sourcesOrigin
            (click)="sourcesOverlayOpenSg.set(!sourcesOverlayOpen)"
          >
            <span>Sources</span>
          </button>

          @if (sourcesOverlayOpen) {
            <atn-ds-attached-overlay
              (backdropClick)="sourcesOverlayOpenSg.set(false)"
              [origin]="sourcesOrigin"
              [offsetX]="0"
              [offsetY]="4"
            >
              <div class="atn-message__sources-overlay">
                <div class="atn-message__sources-overlay-header">
                  <span>Sources</span>
                </div>
                <div class="atn-message__sources-overlay-list">
                  @for (source of sources; track source.key) {
                    <div
                      class="atn-message__sources-overlay-item"
                      [id]="'atn-source-' + source.id"
                      [title]="source.label"
                    >
                      <i
                        class="atn-icon atn-file atn-message__sources-overlay-item-icon"
                      ></i>
                      <span class="atn-message__sources-overlay-item-name">{{
                        source.label
                      }}</span>
                    </div>
                  }
                </div>
              </div>
            </atn-ds-attached-overlay>
          }
        }

        @if (info.length > 0) {
          <atn-assistant-message-info
            [info]="info"
            [status]="status"
          />
        }
      </div>
    </div>
  }

  @if (isFailed) {
    <div class="atn-message__footer">
      @if (sentByUser) {
        <span class="atn-message__error-text">Failed to send</span>
      } @else {
        <span class="atn-message__error-text">Failed to generate response</span>
        <i
          (click)="retry.emit(message)"
          class="atn-icon atn-repeat atn-blue atn-message__retry"
          title="Retry"
        ></i>
      }
    </div>
  }
</div>
